import {Button, Card, DataStatus, showNotification} from 'platform/components';
import {Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {aiAssistantPresentationStore} from '@dms/ai-assistant';
import {useGetAssistantAvailabilityQuery} from '@dms/api/core';

const CRASH_ERROR_NAME = 'Error';
const CRASH_MESSAGE_PREFIX = 'Dev settings crash trigger';
const ERROR_NOTIFICATION_MESSAGE_PREFIX = 'Dev settings error notification';
const ERROR_NOTIFICATION_BUTTON_TITLE = 'Show an error notification';
const ERROR_NOTIFICATION_COOLDOWN_NOTE =
  'The error-notification button goes through the real listener, which offers a report at most once per 10 seconds — wait before pressing again.';

/** Throws during render so the surrounding `DataStatus` boundary catches it, as a real crash would. */
function CrashingContent(props: {crashId: number | null}) {
  if (isNotNil(props.crashId)) {
    throw new Error(`${CRASH_MESSAGE_PREFIX} #${props.crashId}`);
  }

  return null;
}

/**
 * Crashes one card and puts the assistant's crash-report offer on screen. The offer is requested
 * directly rather than left to the crash listener, whose gate limits it to one per unique crash plus
 * a ten-second cooldown — right for users, useless for testing.
 */
export function AiAssistantCrashTrigger() {
  const [crashId, setCrashId] = useState<number | null>(null);
  // The panel drops a crash intent in silence when reporting is unavailable, which looks exactly
  // like a broken button — so show the backend's answer next to the button.
  const availabilityQuery = useGetAssistantAvailabilityQuery();
  const availabilityStatus = match(availabilityQuery)
    .with({isError: true}, () => 'availability request failed — offers are dropped')
    .with({data: {status: 'available', issueReporting: true}}, () => 'reporting available')
    .with({data: Pattern.nonNullable}, ({data}) => `no reporting (status: ${data.status})`)
    .otherwise(() => 'availability unresolved');

  const handleCrash = () => {
    const nextCrashId = Date.now();

    setCrashId(nextCrashId);
    aiAssistantPresentationStore.requestPanelIntent({
      type: 'crashReport',
      crash: {name: CRASH_ERROR_NAME, message: `${CRASH_MESSAGE_PREFIX} #${nextCrashId}`},
      route: window.location.pathname,
    });
  };

  return (
    <Card title="AI assistant crash trigger" variant="inlineGrey">
      <VStack spacing={2}>
        {/* Keyed so each click remounts a fresh boundary instead of staying in its error state. */}
        <DataStatus key={crashId}>
          <CrashingContent crashId={crashId} />
        </DataStatus>

        <Button
          title="Crash a component"
          onClick={handleCrash}
          leftIcon="alert/warning"
          variant="danger"
          data-testid="devSettings-aiAssistantCrashTrigger-crashButton"
        />

        <Button
          title={ERROR_NOTIFICATION_BUTTON_TITLE}
          onClick={() =>
            showNotification.error(`${ERROR_NOTIFICATION_MESSAGE_PREFIX} #${Date.now()}`)
          }
          leftIcon="alert/error"
          variant="secondary"
          data-testid="devSettings-aiAssistantCrashTrigger-errorNotificationButton"
        />

        <Text size="xSmall" color="tertiary">
          {`Crashes one card inside an error boundary — the page keeps running — and opens the assistant with a crash-report offer. Backend: ${availabilityStatus}.`}
        </Text>
        <Text size="xSmall" color="tertiary">
          {ERROR_NOTIFICATION_COOLDOWN_NOTE}
        </Text>
      </VStack>
    </Card>
  );
}
